<script setup lang="ts">
import { computed, reactive, ref, onMounted } from "vue";

// import { useReCaptcha } from "vue-recaptcha-v3";
import VueDatePicker from "@vuepic/vue-datepicker";

declare const turnstile: any;
import "@vuepic/vue-datepicker/dist/main.css";
import dayjs from "dayjs";
import api from "@/http";
import useEventsBus from "@/utils/eventbus";
import useVuelidate from "@vuelidate/core";
import {
  required,
  helpers,
  url,
  minLength,
  maxLength,
} from "@vuelidate/validators";

declare const grecaptcha: any;


// const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();
const now = dayjs();
const { emitt } = useEventsBus();

const sixMonthsAhead = ref(now.add(180, "day").format("YYYY-MM-DD"));
const minDate = computed(() => dayjs().add(-1, "hour").format("YYYY-MM-DD"));
const regexVal = helpers.regex(/^[a-zA-Z0-9-]+$/);

const noBlackListUrl = helpers.withMessage(
  "cannot use this url",
  (value: string) => {
    const blackListRegex = /^(http|https):\/\/(www\.)?(ptpn\.id|palmco\.id|s\.palmco\.id).*$/;
    return !blackListRegex.test(value);
  }
);

const ajaxOnProcess = ref(false);
const defaultInput = ref();
const source = ref("");

const button = ref();

const isSecret = ref(false);
const showPassword = ref(false);

const form = reactive<{
  url: string | null;
  alias: string | null;
  expiry: Date | null;
  captcha_token: string | null;
  password: string | null;
}>({
  url: null,
  alias: null,
  expiry: null,
  captcha_token: null,
  password: null,
});

// Password validation: optional, but if filled must be min 5 chars, max 20 chars, with letters and numbers
const passwordValidator = helpers.withMessage(
  "password must be 5-20 characters with letters and numbers",
  (value: string) => {
    if (!value || value === "") return true;
    if (value.length < 5 || value.length > 20) return false;
    const hasLetter = /[a-zA-Z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    return hasLetter && hasNumber;
  }
);

const mustAhead = (value: any) => {
  if (!form.expiry) return true;
  const a = dayjs();
  const b = a.add(60, "minute");

  return dayjs(value).isAfter(b, "minute");
};

const vuelidateRules = computed(() => ({
  url: {
    required: helpers.withMessage("url must be filled", required),
    mustValidUrl: helpers.withMessage("url format must be valid", url),
    noBlackListUrl,
    $lazy: true,
  },
  alias: {
    minLength: helpers.withMessage("minimum 5 characters", minLength(5)),
    maxLength: helpers.withMessage("maximum 50 characters", maxLength(50)),
    regexVal: helpers.withMessage("must be alphanum and hyphen", regexVal),
    $lazy: true,
  },
  expiry: {
    mustAhead: helpers.withMessage("at least one hour", mustAhead),
  },
  password: {
    passwordValidator,
    $lazy: true,
  },
}));

const v$ = useVuelidate(vuelidateRules, form);

const formatDate = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const hour = date.getHours();
  const minute = date.getMinutes();

  return `${year}-${month}-${day} ${hour}:${minute}:00`;
};



const turnstileContainer = ref(null);
const turnstileWidgetId = ref<string | null>(null);

const onTurnstileSuccess = (token: string) => {
  form.captcha_token = token;
};

onMounted(() => {
  defaultInput.value?.focus();
  setTimeout(() => {
    if (typeof turnstile !== "undefined") {
      turnstileWidgetId.value = turnstile.render(turnstileContainer.value, {
        sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
        callback: onTurnstileSuccess,
      });
    }
  }, 500);
});


const resetForm = () => {
  form.url = null;
  form.alias = null;
  form.expiry = null!;
  form.captcha_token = null;
  form.password = null;
  isSecret.value = false;
  v$.value.$reset();
  if (turnstileWidgetId.value) {
    turnstile.reset(turnstileWidgetId.value);
    form.captcha_token = null;
  }
};
const copyToClipboard = async (valueToCopy: string) => {
  if (location.protocol !== "https:") {
    return emitt("showToast", {
      type: "warning",
      message: "text.this feature is only available on secure connections",
      duration: 3,
      position: "top-center",
    });
  }

  try {
    if (!valueToCopy || valueToCopy === "") return;
    await navigator.clipboard.writeText(valueToCopy);
    return emitt("showToast", {
      type: "success",
      message: "text.url successfully copied to clipboard",
      duration: 3,
      position: "top-center",
    });
  } catch (err) {
    console.error("Failed to copy: ", err);
  }
};

const submitForm = async () => {
  if (ajaxOnProcess.value) return;

  const result = await v$.value.$validate();
  if (!result) return defaultInput.value?.focus();

  try {
    // const token = await recaptcha();
    // form.captcha_token = (token as string) || null;

    if (!form.captcha_token) {
      emitt("showToast", {
        type: "warning",
        message: "Please complete the CAPTCHA challange",
        duration: 3,
        position: "top-center",
      });
      ajaxOnProcess.value = false
      return
    }

    const requestData = {
      url: form.url || "",
      alias: form.alias || "",
      expiry: form.expiry ? dayjs(form.expiry).format("YYYY-MM-DD HH:mm:ss WIB") : "",
      captcha_token: form.captcha_token || "",
      password: isSecret.value ? (form.password || "") : "",
    };

    ajaxOnProcess.value = true;
    const response = await api.post(`shorten`, requestData);
    if (response && response.status === 200) {
      source.value = response.data.alias;
      setTimeout(() => {
        resetForm();
      }, 1500);
    }
  } catch (error: any) {
    source.value = "";
    if (turnstileWidgetId.value) {
      turnstile.reset(turnstileWidgetId.value);
      form.captcha_token = null;
    }
    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      emitt("showMessageBox", {
        message: "error.server is taking too long to respond",
        title: "error.submit failed",
        mode: "error",
        okButtonTitle: "button.ok",
        mustTranslate: true,
      });
    } else {
      emitt("showMessageBox", {
        message: "error." + error.response.data.message,
        title: "error.submit failed",
        mode: "error",
        okButtonTitle: "button.ok",
        mustTranslate: true,
      });
    }

    // ajaxOnProcess.value = false;
  } finally {
    setTimeout(() => {
      ajaxOnProcess.value = false;
    }, 1500);
  }
};
</script>
<template>
  <div>
    <Transition name="fade" mode="out-in" :duration="{ enter: 500, leave: 500 }">
      <div v-if="!ajaxOnProcess && source !== ''"
        class="bg-gray-800 bg-opacity-70 border border-teal-400 rounded-lg p-3 flex justify-between items-center mb-3 space-x-2">
        <p class="text-gray-300">
          {{ $t("text.your short url") }} :
          <a v-tippy="{
            content: $t('tooltip.open the link in a new tab'),
            animation: 'perspective',
            placement: 'top',
          }" class="font-extrabold text-gray-200 hover:text-amber-200" :href="source" target="_blank">{{ source
            }}</a>
        </p>
        <button @click="copyToClipboard(source)" v-tippy="{
          content: $t('tooltip.copy to clipboard'),
          animation: 'perspective',
          placement: 'top',
        }" ref="button" class="bg-teal-500 hover:bg-teal-700 text-gray-100 font-bold p-2 rounded">
          <mdicon name="clipboard-check-outline" class="h-3 w-3" />
        </button>
      </div>
    </Transition>
    <form @submit.prevent="submitForm" class="text-left w-full">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div class="col-span-6">
          <label for="url" class="block mb-2 font-medium text-gray-100">{{ $t("label.paste a long url")
            }}&nbsp;:</label>
          <input ref="defaultInput" type="text" id="url" name="url" v-model="form.url"
            class="bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 rounded-lg block w-full p-2.5"
            :class="{
              'border-red-400 border-2 focus:ring-red-500 focus:border-red-500 ':
                v$.url.$errors.length,
              'border-teal-400 border-2 focus:ring-teal-500 focus:border-teal-500 ':
                !v$.url.$error && v$.url.$dirty,
            }" :placeholder="$t('placeholder.example long url')" />
          <p class="pl-2 text-red-400 text-sm font-semibold" v-for="$error in v$.url.$errors" :key="$error.$property">
            {{ $t("error." + $error.$message) }}
          </p>
        </div>
        <div class="col-span-6 lg:col-span-4">
          <label for="password" class="block mb-2 font-medium text-gray-100">{{ $t("label.special alias")
            }}&nbsp;:</label>
          <input type="text" name="alias" id="alias" v-model="form.alias"
            class="bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600"
            :class="{
              'border-red-400 border-2 focus:ring-red-500 focus:border-red-500 ':
                v$.alias.$errors.length,
              'border-teal-400 border-2 focus:ring-teal-500 focus:border-teal-500 ':
                !v$.alias.$error && v$.alias.$dirty,
            }" :placeholder="$t('placeholder.example alias')" aria-describedby="alias_help" />
          <p class="pl-2 text-red-400 text-sm font-semibold" v-for="$error in v$.alias.$errors" :key="$error.$property">
            {{ $t("error." + $error.$message) }}
          </p>

          <div class="mt-1 text-xs text-gray-100" id="alias_help">
            {{ $t("text.if you fill in the alias the final url will be")
            }}<span class="font-semibold text-yellow-200">https://palmco.id/mantap-kali</span>
          </div>
        </div>
        <div class="col-span-6 lg:col-span-2">
          <label for="password" class="block mb-2 font-medium text-gray-100">{{ $t("label.expired time")
            }}&nbsp;:</label>
          <VueDatePicker v-model="form.expiry" disable-year-select :min-date="minDate" auto-apply
            :max-date="sixMonthsAhead" prevent-min-max-navigation :format="formatDate" :class="{
              'cust__datepicker error': v$.expiry.$errors.length,
              'cust__datepicker success':
                !v$.expiry.$error && v$.expiry.$dirty && form.expiry,
            }"></VueDatePicker>
          <p class="pl-2 text-red-400 text-sm font-semibold" v-for="$error in v$.expiry.$errors"
            :key="$error.$property">
            {{ $t("error." + $error.$message) }}
          </p>
        </div>
        <!-- Toggle Rahasia -->
        <div class="col-span-6 lg:col-span-3">
          <div class="flex items-center">
            <button type="button" @click="isSecret = !isSecret"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out outline-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              :class="isSecret ? 'bg-teal-600' : 'bg-gray-400'" role="switch" :aria-checked="isSecret">
              <span aria-hidden="true"
                class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                :class="isSecret ? 'translate-x-5' : 'translate-x-0'"></span>
            </button>
            <span class="ml-3 text-sm font-medium text-gray-100">
              {{ $t("text.secret") }}
            </span>
          </div>
          <Transition name="fade" mode="out-in">
            <div v-if="isSecret" class="col-span-6 lg:col-span-3 mt-2">
              <label for="password" class="block mb-2 font-medium text-gray-100">
                {{ $t("label.secret password") }}&nbsp;:
              </label>
              <div class="relative">
                <input :type="showPassword ? 'text' : 'password'" name="password" id="password" v-model="form.password"
                  class="bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-400 rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-10"
                  :class="{
                    'border-red-400 border-2 focus:ring-red-500 focus:border-red-500':
                      v$.password.$errors.length,
                    'border-teal-400 border-2 focus:ring-teal-500 focus:border-teal-500':
                      !v$.password.$error && v$.password.$dirty,
                  }" :placeholder="$t('placeholder.enter password')" />
                <button type="button" @click="showPassword = !showPassword"
                  class="absolute inset-y-1 right-1 -top-1 flex items-center pr-3 text-gray-500 hover:text-gray-700 focus:outline-none">
                  <mdicon :name="showPassword ? 'eye-off' : 'eye'" class="h-5 w-5" />
                </button>
              </div>
              <p class="pl-2 text-red-400 dark:text-red-500 text-sm font-semibold" v-for="$error in v$.password.$errors"
                :key="$error.$property">
                {{ $t("error." + $error.$message) }}
              </p>
              <div class="mt-1 text-xs text-red-400 dark:text-red-500" id="password_help">
                {{ $t("text.password help") }}
              </div>
            </div>
          </Transition>
        </div>
        <div class="col-span-6 lg:col-span-3 flex justify-end">
          <div ref="turnstileContainer"></div>
        </div>
        <!-- Password Input (shown when toggle is on) -->

      </div>
      <div class="mt-5 block">
        <button v-if="!ajaxOnProcess" type="submit"
          class="block text-gray-50 bg-teal-600 hover:bg-teal-700 focus:ring-1 focus:outline-none focus:ring-teal-500 font-medium rounded-lg w-full md:w-48 px-5 py-2.5 text-center dark:bg-teal-600 dark:hover:bg-teal-700 dark:focus:ring-teal-800">
          {{ $t("button.process") }}
        </button>
        <div v-else
          class="cursor-default flex justify-center items-center text-white bg-gray-500 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg h-11 w-full md:w-48 px-5 py-2.5 text-center">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
            viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          <span>{{ $t("button.processing") }}...</span>
        </div>
      </div>
    </form>
  </div>
</template>
<style lang="scss">
.dp__input_wrap {
  @apply h-11.5;
}

.dp__input {
  @apply h-full rounded-lg;
}

.mdi-clipboard-check-outline svg {
  @apply w-7 h-7;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.cust__datepicker.error {
  .dp__input_wrap input {
    @apply border-red-400 border-2 focus:ring-red-500 focus:border-red-500;
  }
}

.cust__datepicker.success {
  .dp__input_wrap input {
    @apply border-teal-400 border-2 focus:ring-teal-500 focus:border-teal-500;
  }
}
</style>
